import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Check, Shield, Users, Calendar, ArrowRight, Instagram, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import Logo from '../components/Logo';

import { BACKEND_URL } from '../config';

const WaitlistLanding = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    name: '',
    city: ''
  });
  const [loading, setLoading] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);

  useEffect(() => {
    console.log('[WaitlistLanding] Component mounted');
    console.log('[WaitlistLanding] BACKEND_URL:', BACKEND_URL);
    fetchWaitlistCount();
  }, []);

  const fetchWaitlistCount = async () => {
    try {
      console.log('[WaitlistLanding] Fetching count from:', `${BACKEND_URL}/api/waitlist/count`);
      const response = await axios.get(`${BACKEND_URL}/api/waitlist/count`);
      console.log('[WaitlistLanding] Count received:', response.data.count);
      setWaitlistCount(response.data.count);
    } catch (error) {
      console.error('[WaitlistLanding] Error fetching waitlist count:', error);
      // Don't show error to user, just set count to 0
      setWaitlistCount(0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Check if city is Vadodara
      const cityLower = formData.city.toLowerCase().trim();
      const isVadodara = cityLower === 'vadodara' || cityLower === 'baroda';
      
      if (isVadodara) {
        // Redirect Vadodara users to signup/login
        toast.success('Vadodara is live! Please create your account to join.');
        localStorage.setItem('signupCity', formData.city);
        localStorage.setItem('signupName', formData.name);
        localStorage.setItem('signupEmail', formData.email);
        localStorage.setItem('signupPhone', formData.phone);
        setTimeout(() => {
          navigate('/register');
        }, 1000);
      } else {
        // Non-Vadodara users go to waitlist
        const response = await axios.post(`${BACKEND_URL}/api/waitlist/join`, formData);
        
        if (response.data.success) {
          toast.success(`We're launching soon in ${formData.city}! You're on the waitlist.`);
          setFormData({ email: '', phone: '', name: '', city: '' });
          fetchWaitlistCount();
          
          // Scroll to confirmation message
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          toast.error(response.data.message);
        }
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone input
    if (name === 'phone') {
      // Remove all non-digit characters
      let cleaned = value.replace(/\D/g, '');
      
      // If starts with 91, keep it; otherwise add +91 prefix
      if (cleaned.startsWith('91') && cleaned.length > 2) {
        cleaned = cleaned;
      } else if (!cleaned.startsWith('91') && cleaned.length > 0) {
        cleaned = '91' + cleaned;
      }
      
      // Limit to 12 digits (91 + 10 digits)
      if (cleaned.length > 12) {
        cleaned = cleaned.slice(0, 12);
      }
      
      // Format as +91 XXXXX XXXXX
      let formatted = '';
      if (cleaned.length > 0) {
        formatted = '+91';
        if (cleaned.length > 2) {
          formatted += ' ' + cleaned.slice(2, 7);
        }
        if (cleaned.length > 7) {
          formatted += ' ' + cleaned.slice(7, 12);
        }
      }
      
      setFormData({
        ...formData,
        phone: formatted
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Removed testimonials functionality

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image - Optimized: 1600w WebP @ 295KB (was 1050KB) */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1491438590914-bc09fcaaf77a?fm=webp&q=75&w=1600&fit=crop"
            alt=""
            width="1600"
            height="1067"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-center"
            style={{ minHeight: '100vh' }}
          />
          {/* Overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        </div>
        
        {/* Header */}
        <header className="absolute top-0 left-0 right-0 z-20 px-4 py-4 md:px-12 md:py-6">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-3">
              <Logo size="md" />
              <span className="text-xl md:text-2xl font-serif text-white tracking-wide drop-shadow-lg">Ophilia</span>
            </div>
            <button 
              onClick={() => navigate('/login')}
              className="text-white text-xs md:text-sm tracking-wide hover:text-[#FF7A45] transition-colors px-3 py-2 md:px-4 md:py-2 rounded-lg bg-black/30 backdrop-blur-sm hover:bg-black/40"
            >
              <span className="hidden sm:inline">Already a member? </span>Sign In
            </button>
          </div>
        </header>

        {/* Hero Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-6 text-center pt-20 md:pt-0">
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-serif text-white mb-4 md:mb-6 leading-tight">
            Ophilia — Your exclusive<br />circle of women
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white/90 mb-3 md:mb-4 font-light">
            A new women-only social club for meaningful friendships, curated experiences, and real connections.
          </p>
          <p className="text-base md:text-lg text-white/80 mb-8 md:mb-12 max-w-2xl mx-auto">
            Launching first in Vadodara — expanding soon across major Indian cities.
          </p>
          
          {/* Primary CTAs - Signup/Login */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 md:p-10 max-w-2xl mx-auto shadow-2xl mb-6">
            <p className="text-xs md:text-sm uppercase tracking-wider text-[#FF7A45] font-semibold mb-6">
              Vadodara is Live!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <button
                onClick={() => navigate('/register')}
                className="flex-1 bg-black text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-[#FF7A45] transition-all duration-300 flex items-center justify-center gap-2"
              >
                Create Account
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => navigate('/login')}
                className="flex-1 bg-white text-black border-2 border-black py-4 px-6 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-all duration-300"
              >
                Sign In
              </button>
            </div>
            
            <div className="flex items-center gap-2 text-sm text-stone-500">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span>For Vadodara residents only</span>
            </div>
          </div>
          
          {/* Secondary CTA - Waitlist */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 md:p-8 max-w-2xl mx-auto shadow-xl">
            <p className="text-xs md:text-sm uppercase tracking-wider text-stone-600 font-semibold mb-4">
              Other Cities - Join Waitlist
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3.5 md:px-5 md:py-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF7A45] focus:ring-2 focus:ring-[#FF7A45]/20 transition-colors bg-white text-base"
                  required
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email address"
                  inputMode="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3.5 md:px-5 md:py-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF7A45] focus:ring-2 focus:ring-[#FF7A45]/20 transition-colors bg-white text-base"
                  required
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+91 XXXXX XXXXX"
                    inputMode="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3.5 md:px-5 md:py-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF7A45] focus:ring-2 focus:ring-[#FF7A45]/20 transition-colors bg-white text-base"
                    pattern="^\+91 [0-9]{5} [0-9]{5}$"
                    title="Enter a valid Indian mobile number"
                    required
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-stone-400">
                    Indian mobile
                  </span>
                </div>
                <input
                  type="text"
                  name="city"
                  placeholder="Your City"
                  value={formData.city}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3.5 md:px-5 md:py-4 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF7A45] focus:ring-2 focus:ring-[#FF7A45]/20 transition-colors bg-white text-base"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-black text-white py-4 md:py-4 rounded-lg font-medium hover:bg-[#FF7A45] active:scale-98 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-base md:text-base min-h-[50px] touch-manipulation"
              >
                {loading ? 'Submitting...' : 'Submit'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>

            <p className="text-xs text-stone-500 mt-4 text-center">
              Vadodara users will be directed to signup. Other cities added to waitlist.
            </p>
            
            {waitlistCount > 0 && (
              <p className="text-xs md:text-sm text-stone-500 mt-2">
                <span className="font-semibold text-[#FF7A45]">{waitlistCount}</span> women on the waitlist
              </p>
            )}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-6 md:mb-8">Why Ophilia Exists</h2>
          </div>
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <p className="text-base md:text-lg text-stone-600 leading-relaxed mb-4">
              Making real friends as an adult is hard — especially when life takes you to new cities for studies, jobs, or marriage. I've lived that journey myself. Every move felt like starting over, without anyone who truly understood me.
            </p>
            <p className="text-base md:text-lg text-stone-600 leading-relaxed mb-6">
              Ophilia was born from that feeling. My vision is simple: no woman should feel alone. I want to build a circle where sisterhood feels natural, safe, and genuinely fulfilling — powered by shared interests and meaningful experiences.
            </p>
            <p className="text-lg md:text-xl font-serif text-stone-800 text-right">
              — Founder, Priya
            </p>
          </div>
        </div>
      </section>

      {/* What's Inside the Club Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-4 md:mb-6">What You'll Find Inside Ophilia</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {/* Events */}
            <div className="bg-[#F5F1E8] rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#FF7A45] rounded-full flex items-center justify-center mb-6">
                <Calendar className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Events</h3>
              <p className="text-stone-600 leading-relaxed mb-2">
                Carefully curated women-only gatherings — from brunches to workshops to community experiences.
              </p>
              <p className="text-sm text-stone-500 italic">
                Events are paid, and any member can create an event.
              </p>
            </div>

            {/* Quick Meets */}
            <div className="bg-[#F5F1E8] rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#FF7A45] rounded-full flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Quick Meets</h3>
              <p className="text-stone-600 leading-relaxed mb-2">
                Casual, spontaneous hangouts that any member can create or join.
              </p>
              <p className="text-sm text-stone-500 italic">
                Always free, always easy.
              </p>
            </div>

            {/* Curated Connections */}
            <div className="bg-[#F5F1E8] rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#FF7A45] rounded-full flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Curated Connections</h3>
              <p className="text-stone-600 leading-relaxed mb-2">
                Get matched based on your interests and lifestyle.
              </p>
              <p className="text-sm text-stone-500 italic">
                No random swiping — only meaningful introductions.
              </p>
            </div>

            {/* Messaging */}
            <div className="bg-[#F5F1E8] rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#FF7A45] rounded-full flex items-center justify-center mb-6">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Messaging</h3>
              <p className="text-stone-600 leading-relaxed">
                Chat privately with your matches and plan meetups effortlessly.
              </p>
            </div>

            {/* Safety & Moderation */}
            <div className="bg-[#F5F1E8] rounded-2xl p-8 hover:shadow-xl transition-shadow md:col-span-2 lg:col-span-2">
              <div className="w-16 h-16 bg-[#FF7A45] rounded-full flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-serif mb-4">Safety & Moderation</h3>
              <p className="text-stone-600 leading-relaxed mb-2">
                Every profile is manually approved within 24 hours to maintain authenticity.
              </p>
              <p className="text-sm text-stone-800 font-semibold">
                Women only. Zero compromise.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Plans Section */}
      <section id="membership" className="py-16 md:py-24 px-4 md:px-6 bg-[#F5F1E8]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-4 md:mb-6">Membership</h2>
            <p className="text-base md:text-xl text-stone-600 max-w-2xl mx-auto mb-4">
              Your first <span className="font-bold text-[#FF7A45] text-2xl md:text-3xl">THREE months</span> are absolutely free from the date of joining!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
            {/* Monthly Plan */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-2xl transition-all hover:scale-105">
              <div className="text-center">
                <p className="text-sm uppercase tracking-wider text-stone-500 mb-2">Monthly</p>
                <div className="mb-6">
                  <span className="text-5xl font-serif text-stone-800">₹99</span>
                  <span className="text-stone-500">/month</span>
                </div>
                <button 
                  onClick={() => navigate('/auth?plan=monthly')}
                  className="w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-[#FF7A45] transition-all"
                >
                  Start Free Month
                </button>
              </div>
            </div>

            {/* 6 Months Plan */}
            <div className="bg-[#FF7A45] text-white rounded-2xl p-8 hover:shadow-2xl transition-all hover:scale-105 relative">
              <div className="absolute -top-3 right-6 bg-black text-white text-xs px-3 py-1 rounded-full uppercase tracking-wider">
                Popular
              </div>
              <div className="text-center">
                <p className="text-sm uppercase tracking-wider text-white/80 mb-2">6 Months</p>
                <div className="mb-2">
                  <span className="text-5xl font-serif">₹499</span>
                </div>
                <p className="text-sm text-white/80 mb-6">Save 16%</p>
                <button 
                  onClick={() => navigate('/auth?plan=6months')}
                  className="w-full bg-white text-[#FF7A45] py-3 rounded-lg font-medium hover:bg-black hover:text-white transition-all"
                >
                  Start Free Month
                </button>
              </div>
            </div>

            {/* Yearly Plan */}
            <div className="bg-white rounded-2xl p-8 hover:shadow-2xl transition-all hover:scale-105">
              <div className="text-center">
                <p className="text-sm uppercase tracking-wider text-stone-500 mb-2">Yearly</p>
                <div className="mb-2">
                  <span className="text-5xl font-serif text-stone-800">₹999</span>
                  <span className="text-stone-500">/year</span>
                </div>
                <p className="text-sm text-stone-500 mb-6">Save 17%</p>
                <button 
                  onClick={() => navigate('/auth?plan=yearly')}
                  className="w-full bg-black text-white py-3 rounded-lg font-medium hover:bg-[#FF7A45] transition-all"
                >
                  Start Free Month
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Join Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-4 md:mb-6">How to Join</h2>
          </div>

          <div className="space-y-8">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#FF7A45] rounded-full flex items-center justify-center text-white font-serif text-xl">
                1
              </div>
              <div>
                <h3 className="text-xl font-serif mb-2">Open the App</h3>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#FF7A45] rounded-full flex items-center justify-center text-white font-serif text-xl">
                2
              </div>
              <div>
                <h3 className="text-xl font-serif mb-2">Create your profile</h3>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#FF7A45] rounded-full flex items-center justify-center text-white font-serif text-xl">
                3
              </div>
              <div>
                <h3 className="text-xl font-serif mb-2">Upload photo + click a selfie</h3>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#FF7A45] rounded-full flex items-center justify-center text-white font-serif text-xl">
                4
              </div>
              <div>
                <h3 className="text-xl font-serif mb-2">Our team manually approves your profile within 48 hours</h3>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-[#FF7A45] rounded-full flex items-center justify-center text-white font-serif text-xl">
                5
              </div>
              <div>
                <h3 className="text-xl font-serif mb-4">Once approved, you can:</h3>
                <ul className="space-y-2 text-stone-600">
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-[#FF7A45] flex-shrink-0 mt-0.5" />
                    <span>Join Curated Events</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-[#FF7A45] flex-shrink-0 mt-0.5" />
                    <span>Plan meet-ups</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-[#FF7A45] flex-shrink-0 mt-0.5" />
                    <span>Explore curated connections</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-[#FF7A45] flex-shrink-0 mt-0.5" />
                    <span>Message your Friends</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <button 
              onClick={() => navigate('/register')}
              className="bg-black text-white px-12 py-4 rounded-lg font-medium hover:bg-[#FF7A45] transition-all text-lg"
            >
              Get Started
            </button>
          </div>
        </div>
      </section>

      {/* Waitlist for Upcoming Cities Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-[#F5F1E8]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif mb-6 md:mb-8">Your City Coming Soon</h2>
          <p className="text-base md:text-xl text-stone-600 leading-relaxed mb-8 md:mb-12 max-w-2xl mx-auto">
            We're launching next in Ahmedabad, Surat, Mumbai, Pune, Bengaluru, Delhi, and more.<br />
            Join the waitlist to unlock early access and founding-member perks.
          </p>
          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-black text-white px-12 py-4 rounded-lg font-medium hover:bg-[#FF7A45] transition-all text-lg"
          >
            Join the Waitlist
          </button>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 px-4 md:px-6 bg-black text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-6xl font-serif mb-4 md:mb-6">Ready to find your circle?</h2>
          <p className="text-base md:text-xl mb-8 md:mb-12 text-gray-300 px-2">
            Join the waitlist and be part of something meaningful
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="bg-[#FF7A45] text-white px-8 md:px-12 py-3.5 md:py-4 rounded-lg font-medium hover:bg-[#ff8c5c] active:scale-98 transition-all duration-300 text-base md:text-lg min-h-[50px] touch-manipulation"
          >
            Request Your Invitation
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F1E8] py-12 md:py-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <Logo size="lg" />
                <span className="text-2xl font-serif">Ophilia</span>
              </div>
              <p className="text-stone-600 mb-6 leading-relaxed">
                India's first premium women-only club for authentic friendships and curated experiences.
              </p>
              <div className="flex gap-4">
                <a href="https://www.instagram.com/ophilia.app" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-[#FF7A45] hover:text-white transition-all">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="mailto:hello@ophilia.app" className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:bg-[#FF7A45] hover:text-white transition-all">
                  <Mail className="w-5 h-5" />
                </a>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4 text-lg">Quick Links</h3>
              <ul className="space-y-3">
                <li><a href="#about" className="text-stone-600 hover:text-[#FF7A45] transition-colors">About Us</a></li>
                <li><a href="#how-it-works" className="text-stone-600 hover:text-[#FF7A45] transition-colors">How It Works</a></li>
                <li><a href="#events" className="text-stone-600 hover:text-[#FF7A45] transition-colors">Events</a></li>
                <li><a href="#faqs" className="text-stone-600 hover:text-[#FF7A45] transition-colors">FAQs</a></li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4 text-lg">Legal</h3>
              <ul className="space-y-3">
                <li><a href="#privacy" className="text-stone-600 hover:text-[#FF7A45] transition-colors">Privacy Policy</a></li>
                <li><a href="#terms" className="text-stone-600 hover:text-[#FF7A45] transition-colors">Terms of Service</a></li>
                <li><a href="#guidelines" className="text-stone-600 hover:text-[#FF7A45] transition-colors">Community Guidelines</a></li>
                <li><a href="#contact" className="text-stone-600 hover:text-[#FF7A45] transition-colors">Contact Us</a></li>
              </ul>
            </div>
          </div>

          {/* Legal Content Sections */}
          <div className="border-t border-gray-300 pt-12 space-y-12">
            {/* Privacy Policy */}
            <div id="privacy" className="max-w-4xl mx-auto">
              <h3 className="text-2xl font-serif mb-4 text-stone-800">Privacy Policy</h3>
              <div className="text-sm text-stone-600 leading-relaxed space-y-3">
                <p>Ophilia collects and processes only the information necessary to operate, secure, and improve the platform. This may include profile details, verification data, usage data, and communications within the app.</p>
                <p>We do not sell personal data to third parties.</p>
                <p>Data may be shared only with service providers required for app functionality, legal compliance, or security purposes.</p>
                <p>By using Ophilia, you consent to this data collection and processing.</p>
                <p>Users may request account and data deletion by contacting us, subject to legal and operational requirements.</p>
              </div>
            </div>

            {/* Terms of Service */}
            <div id="terms" className="max-w-4xl mx-auto">
              <h3 className="text-2xl font-serif mb-4 text-stone-800">Terms of Service</h3>
              <div className="text-sm text-stone-600 leading-relaxed space-y-3">
                <p>Ophilia is a platform that facilitates discovery and connection between users. We do not act as an event organizer, payment intermediary, guarantor, or mediator unless explicitly stated.</p>
                <p className="font-semibold">By accessing or using Ophilia, you agree to:</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Provide accurate and truthful information</li>
                  <li>Use the platform lawfully and responsibly</li>
                  <li>Accept full responsibility for your interactions, events, and transactions</li>
                </ul>
                <p className="font-semibold">Ophilia is not liable for:</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>User-generated content</li>
                  <li>Off-platform interactions or payments</li>
                  <li>Disputes, losses, injuries, or damages arising from events or meet-ups</li>
                </ul>
                <p>We reserve the right to suspend, restrict, or permanently terminate accounts at our sole discretion, with or without notice.</p>
              </div>
            </div>

            {/* Community Guidelines */}
            <div id="guidelines" className="max-w-4xl mx-auto">
              <h3 className="text-2xl font-serif mb-4 text-stone-800">Community Guidelines</h3>
              <div className="text-sm text-stone-600 leading-relaxed space-y-3">
                <p>Ophilia is a women-only, platonic community.</p>
                <p className="font-semibold">The following are strictly prohibited:</p>
                <ul className="list-disc list-inside space-y-1 pl-4">
                  <li>Harassment, abuse, hate speech, or discrimination</li>
                  <li>Scams, misleading events, or financial manipulation</li>
                  <li>Impersonation or false representation</li>
                  <li>Solicitation or misuse of the platform</li>
                </ul>
                <p>Violations may result in immediate suspension or permanent removal, without warning.</p>
                <p>Repeated or severe violations may be reported to relevant authorities where required by law.</p>
              </div>
            </div>

            {/* Contact Us */}
            <div id="contact" className="max-w-4xl mx-auto">
              <h3 className="text-2xl font-serif mb-4 text-stone-800">Contact Us</h3>
              <div className="text-sm text-stone-600 leading-relaxed space-y-3">
                <p><span className="font-semibold">Email:</span> hello@ophilia.app</p>
                <p>For support, policy questions, or legal requests, contact us via email.</p>
                <p>Response times may vary based on request type.</p>
              </div>
            </div>
          </div>

          {/* Newsletter */}
          <div className="border-t border-gray-300 pt-12 mt-12">
            <div className="max-w-2xl mx-auto text-center">
              <h3 className="text-2xl font-serif mb-4">Stay Updated</h3>
              <p className="text-stone-600 mb-6">Get early access to new experiences and community updates</p>
              <form className="flex gap-4 max-w-md mx-auto">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-5 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF7A45] bg-white"
                />
                <button
                  type="submit"
                  className="bg-black text-white px-8 py-3 rounded-lg font-medium hover:bg-[#FF7A45] transition-all"
                >
                  Subscribe
                </button>
              </form>
            </div>
          </div>

          <div className="text-center mt-12 pt-8 border-t border-gray-300">
            <p className="text-stone-500 text-sm">
              © 2024 Ophilia. All rights reserved. Made with ❤️ in India
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WaitlistLanding;
